import Enumerable from "linq";
import * as Moment from "moment";
import store from "../../../../store";
import {
  ExecuteRequest,
  ExternalScriptExecuteResult,
  ScriptLinkItem,
} from "../../../../domain/localhost/items";
import { IInternalScriptProcessible } from "..";
import { LoggerFactory } from "../../../../utilities/logger";
import { EraAsJapaneseCalendar } from "../../../../domain/system";
import { Arguments } from ".";

// 外部スクリプト互換の内部スクリプト実装.
// ConvertReplacer機能拡張時は同様に対処要.
// (2019/4/18現在、最終更新は2017/8/8で、2年近く更新されてないのでそうそう変わらないと思いますが.)
export class ConvertReplacer implements IInternalScriptProcessible {
  constructor() {
    this._converterFactories = Enumerable.from(
      [
        // 対応するaction用のconverter生成実装をここに追加する.
        { key: "check", value: (a: Arguments): IConvertible => new Checker(a) },
        { key: "split", value: (a: Arguments): IConvertible => new StringSplitter(a) },
        { key: "numeric", value: (a: Arguments): IConvertible => new NumericSplitter(a) },
        // TODO: convertの実装.(徳島大使用フォーマットなし.現状でも外部スクリプトが動作しますが...)
      ]
    )
    .toDictionary(f => f.key, f => f.value);
  }

  private readonly _converterFactories: Enumerable.IDictionary<string, (a: Arguments) => IConvertible>;

  private createConverter(request: ExecuteRequest): IConvertible {
    return this._converterFactories.get(this.toKey(request))(this.toArguments(request));
  }

  isProcessible(request: ExecuteRequest): boolean {
    const configuration = store.state.configuration.behavior.localhost.internalScript.scripts.convertReplacer;
    return configuration.isEnable
        && (new RegExp(configuration.pathExpression, "i")).test(request.exePath)
        && this._converterFactories.contains(this.toKey(request));
  }

  private toKey(request: ExecuteRequest): string {
    return this.toArguments(request).getValue("-action");
  }

  private toArguments(request: ExecuteRequest): Arguments {
    return new Arguments(request.arguments);
  }

  async process(request: ExecuteRequest): Promise<ExternalScriptExecuteResult> {
    return this.toResult(request, await this.createConverter(request).convert(this.toValues(request)));
  }

  private toValues(request: ExecuteRequest): Enumerable.IDictionary<string, string> {
    return Enumerable.from(request.scriptLinkItems)
      .toDictionary(i => i.name, i => i.value);
  }

  private toResult(request: ExecuteRequest, results: Enumerable.IDictionary<string, string>): ExternalScriptExecuteResult {
    const result = new ExternalScriptExecuteResult();
    result.isComplete = true;
    result.name = undefined;
    result.values = [];
    result.compressedCsvText = "";
    result.scriptLinkResults = Enumerable.from(request.scriptLinkItems)
      .where(i => results.contains(i.name))
      .select(i => this.toClonedItem(i, results.get(i.name)))
      .toArray();
    return result;
  }

  private toClonedItem(source: ScriptLinkItem, value: string): ScriptLinkItem {
    // TODO: clone使ったほうが良さそう
    const result = new ScriptLinkItem();
    result.name = source.name;
    result.subName = source.subName;
    result.objectId = source.objectId;
    result.itemName = source.itemName;
    result.value = value;
    return result;
  }
}

interface IConvertible {
  convert(values: Enumerable.IDictionary<string, string>): Promise<Enumerable.IDictionary<string, string>>;
}

abstract class AbstractConverter implements IConvertible {
  constructor(private readonly _arguments: Arguments) {
    this._logger.debug("converter: " + _arguments.toString());
    this._shouldConvertToHalf = this.containsArgument("-force-narrow");
    this._validCharacters = this.toCharacters(this.getArgument("-allow-check"));
    this._invalidCharacters = this.toCharacters(this.getArgument("-extract-check"));
  }
  private readonly _shouldConvertToHalf: boolean;
  private readonly _validCharacters: string[];
  private readonly _invalidCharacters: string[];
  protected readonly _logger = LoggerFactory.getLogger();

  private toCharacters(value: string): string[] {
    return value 
      ? value.split("")
      : null;
  }

  async convert(values: Enumerable.IDictionary<string, string>): Promise<Enumerable.IDictionary<string, string>> {
    this._logger.debug("convert(): \n" + this.valuesToString(values));
    const originalSource = this.toOriginalSource(values);
    const converted = await this.convertValues(this.form(originalSource), values, originalSource);
    this._logger.debug("converted: \n" + this.valuesToString(converted));
    return converted;
  }

  private valuesToString(values: Enumerable.IDictionary<string, string>): string {
    return values.toEnumerable()
      .select(v => "[" + v.key + "]" + v.value)
      .toArray()
      .join("\n");
  }

  private toOriginalSource(values: Enumerable.IDictionary<string, string>): string {
    return values.get(this.getArgument("-source"));
  }

  private form(originalSource: string): string {
    const source = this.preprocessSourceText(originalSource);
    return this.toValidString(this._shouldConvertToHalf ? this.toHalfString(source) : source);
  }

  private toHalfString(value: string): string {
    return value
      .replace(/[！-～]/g, s => String.fromCharCode(s.charCodeAt(0) - 0xFEE0))
      .replace(/　/g, " ")
      .replace(/￥/g, "\\")
      .replace(/[“”]/g, "\"")
      .replace(/[‘’]/g, "\'");
  }

  private toValidString(value: string): string {
    return Enumerable.from(value.split(""))
      .where(c => this.isValidCharacter(c))
      .toArray()
      .join("");
  }

  private isValidCharacter(character: string): boolean {
    return (!this._validCharacters || (0 <= this._validCharacters.indexOf(character)))
        && (!this._invalidCharacters || (this._invalidCharacters.indexOf(character) < 0));
  }

  protected containsArgument(key: string): boolean {
    return this._arguments.contains(key);
  }

  protected getArgument(key: string): string {
    return this._arguments.getValue(key);
  }

  protected get emptyValues(): Enumerable.IDictionary<string, string> {
    return Enumerable.from([] as { key: string, value: string }[])
      .toDictionary(p => p.key, p => p.value);
  }

  protected abstract preprocessSourceText(source: string): string;
  protected abstract convertValues(source: string, values: Enumerable.IDictionary<string, string>, originalSource: string): Promise<Enumerable.IDictionary<string, string>>;
}

class Checker extends AbstractConverter {
  constructor(_arguments: Arguments) {
    super(_arguments);
    this._prefix = this.getArgument("-dest-header");
    this._on = this.getArgument("-on");
    this._off = this.getArgument("-off");
  }

  private readonly _prefix: string;
  private readonly _on: string;
  private readonly _off: string;

  protected preprocessSourceText(source: string): string {
    return source;
  }

  protected async convertValues(source: string, values: Enumerable.IDictionary<string, string>, originalSource: string): Promise<Enumerable.IDictionary<string, string>> {
    return values.toEnumerable()
      .where(v => this.shouldConvert(v.key))
      .select(v => this.convertValue(v.key, source))
      .toDictionary(v => v.key, v => v.value);
  }

  private shouldConvert(key: string): boolean {
    return (key.indexOf(this._prefix) === 0);
  }

  private convertValue(key: string, source: string): { key: string, value: string } {
    return {
      key,
      value: (key === this._prefix + source) ? this._on : this._off,
    };
  }
}

class StringSplitter extends AbstractConverter {
  constructor(_arguments: Arguments) {
    super(_arguments);
    this._indexes = Enumerable.from(this.getArgument("-dest-header").split(","))
      .select(v => v.split("="))
      .where(v => v.length === 2)
      .toDictionary(v => v[0], v => parseInt(v[1], 10));
    this._shouldIgnoreFilledValues = !this.containsArgument("-force_overwrite");
    this._shouldIgnoreEmptySource = this.containsArgument("-ignore_empty");
    this._previousSourceKey = this.getArgument("-overwrite_key");
    this._isSourceCalendar = this.containsArgument("-calendar");
    this._isJapaneseEra = this.containsArgument("-wareki");
    this._shouldAddZero = !this.containsArgument("-issuppress");
  }

  private readonly _indexes: Enumerable.IDictionary<string, number>;
  private readonly _shouldIgnoreFilledValues: boolean;
  private readonly _shouldIgnoreEmptySource: boolean;
  private readonly _previousSourceKey: string;
  private readonly _isSourceCalendar: boolean;
  private readonly _isJapaneseEra: boolean;
  private readonly _shouldAddZero: boolean;

  protected preprocessSourceText(source: string): string {
    if (!source || !this._isSourceCalendar) { return source; }
    if (this._isJapaneseEra && !this._shouldAddZero) {
      return this.convertFirstYear(source);
    }
    if (this._shouldAddZero) {
      return this.fillCalendarValueWithZero(this.convertFirstYear(source));
    }
    return source;
  }

  private fillCalendarValueWithZero(source: string): string {
    const yearLength = this._isJapaneseEra ? 2 : 4;
    return source
      .replace(/(\d+)( *年)/, (match, ...values) => values[0].padStart(yearLength, "0") + values[1])
      .replace(/(\d+)( *月)/, (match, ...values) => values[0].padStart(2, "0") + values[1])
      .replace(/(\d+)( *日)/, (match, ...values) => values[0].padStart(2, "0") + values[1])
      .replace(/^([^\d\/\.]*)(\d+)( *頃)/, (match, ...values) => values[0] + values[1].padStart(yearLength, "0") + values[2])
      .replace(/^([^\d\/\.]*)(\d+)( *[\/\.] *)(\d+)( *頃)/, (match, ...values) => values[0] + values[1].padStart(yearLength, "0") + values[2] + values[3].padStart(2, "0") + values[4])
      .replace(/^([^\d\/\.]*)(\d+)( *[\/\.] *)(\d+)( *[上中下]旬)/, (match, ...values) => values[0] + values[1].padStart(yearLength, "0") + values[2] + values[3].padStart(2, "0") + values[4])
      .replace(/^([^\d\/\.]*)(\d+)( *[\/\.] *)(\d+)( *[\/\.] *)(\d+)/, (match, ...values) => values[0] + values[1].padStart(yearLength, "0") + values[2] + values[3].padStart(2, "0") + values[4] + values[5].padStart(2, "0"));
  }

  /**
   * カレンダー和暦分割の場合に「元 年」を数値分割できる値に置換する
   */
  private convertFirstYear(source: string): string {
    const value = this._isJapaneseEra ? "1" : "0";
    return source.replace(/(元)( *年)/g, value + "$2");
  }

  protected async convertValues(source: string, values: Enumerable.IDictionary<string, string>, originalSource: string): Promise<Enumerable.IDictionary<string, string>> {
    if (this.shouldIgnoreNoChanged && (this.toPreviousSource(values) === source)) {
      this._logger.debug("同方向なので変換しない.");
      return this.emptyValues;
    }
    // ソースの空判定はcalendar変換後の方がいいと思うが、、、現ConvertReplacerの実装に合わせる.    
    if (this._shouldIgnoreEmptySource && !source) {
      this._logger.debug("ソースが空なので変換しない.");
      return this.emptyValues;
    }
    if (this._shouldIgnoreFilledValues && values.toEnumerable().any(v => (0 <= this.toIndex(v.key)) && !!v.value)) {
      this._logger.debug("値が設定済なので変換しない.");
      return this.emptyValues;
    }
    const convertedSource = this.convertSourceValue(source, originalSource);
    return values.toEnumerable()
      .where(v => this.shouldConvert(v.key, convertedSource))
      .select(v => this.convertValue(v.key, convertedSource))
      .toDictionary(v => v.key, v => v.value);
  }

  private convertSourceValue(source: string, originalSource: string): string {
    // 「元 年」を「0000」に置換したとき、全て0で未選択と判定されてしまわないよう対策している
    const converted = (this._isSourceCalendar && !(/元 *年/.test(originalSource)) && source.replace(/0+/, "") === "")
      ? ""
      : source;
    
    if (this._isSourceCalendar && this._isJapaneseEra && converted) {
      // 和暦の分割対応
      const moment = Moment(converted, "YYYYMMDD", true);
      if (!moment.isValid) { return converted; }
      const date = moment.toDate();
      if (date.toString() === "Invalid Date") { return converted; }
      const era = EraAsJapaneseCalendar.from(date);
      return ("00" + era.year).slice(-2) + moment.format("MMDD");
    }
    return converted;
  }

  private get shouldIgnoreNoChanged(): boolean {
    return !!this._previousSourceKey;
  }

  private toPreviousSource(values: Enumerable.IDictionary<string, string>): string {
    return values.get(this._previousSourceKey);
  }

  private shouldConvert(key: string, source: string): boolean {
    const index = this.toIndex(key);
    return (key === this._previousSourceKey)  // 前回値.
        || (0 <= index);                      // split先.
  }

  private toIndex(key: string): number {
    const found = this._indexes.toEnumerable()
      .firstOrDefault(i => key.indexOf(i.key) === 0);
    return found
      ? parseInt(key.replace(found.key, ""), 10) + found.value - 1
      : -1;
  }

  private convertValue(key: string, source: string): { key: string, value: string } {
    const index = this.toIndex(key);
    return {
      key,
      value: (key === this._previousSourceKey)
        ? source                                                  // 前回値.
        : (index < source.length) ? source.substr(index, 1) : "", // split先.
    };
  }
}

class NumericSplitter extends AbstractConverter {
  constructor(_arguments: Arguments) {
    super(_arguments);
    this._keys = this.splitKeys(this.getArgument("-dest-columns"));
    this._lengthOfDecimalPart = this.toInteger(this.getArgument("-decimallength"));
    this._lengthOfIntegerPart = this._keys.length - this._lengthOfDecimalPart;
  }

  private readonly _keys: string[];
  private readonly _lengthOfDecimalPart: number;
  private readonly _lengthOfIntegerPart: number;

  private splitKeys(keys: string): string[] {
    // \^を救うために一旦「￥￥￥￥^」に置き換えて回避してる.(一時置換用の文字列もConvertReplacer.exe実装に準拠.)
    return Enumerable.from(keys.replace("\\^", "￥￥￥￥＾").split("^"))
      .select(k => k.replace("￥￥￥￥＾", "^"))
      .toArray();
  }

  private toInteger(value: string, defaultValue = 0): number {
    try {
      const result = parseInt(value, 10);
      return (!isNaN(result) && (result !== null))
        ? result
        : defaultValue;
    } catch {
      return defaultValue;
    }
  }

  protected preprocessSourceText(source: string): string {
    return source;
  }

  protected async convertValues(source: string, values: Enumerable.IDictionary<string, string>, originalSource: string): Promise<Enumerable.IDictionary<string, string>> {
    try {
      const formattedSource = this.formatSource(source);
      return Enumerable.from(this._keys)
        .select((k, i) => this.toPair(k, formattedSource, i))
        .toDictionary(p => p.key, p => p.value);
    } catch (e) {
      this._logger.error(e);
      return this.emptyValues;
    }
  }

  private formatSource(source: string): string {
    // decimalLengthが未指定(0)の場合は入力桁数を考慮して値を切り出す
    return (0 < this._lengthOfDecimalPart)
      ? this.formatDecimalSource(source)
      : this.trimByOutputLength(source);
  }

  private formatDecimalSource(source: string): string {
    // 数値じゃない場合もそのまま処理する模様.(ConvertReplacer.exeの実装に合わせる.)
    return this.toIntegerPart(source) + this.toDecimalPart(source);
  }

  private trimByOutputLength(source: string): string {
    const outputLength = this._keys.length;
    const trimmed = (source.length <= outputLength)
      ? source
      : source.substring(0, outputLength).replace(/\.+$/, "");
    return this.fillTextWithIntegerLength(trimmed);
  }

  private toIntegerPart(source: string): string {
    const value = source.split(".")[0];
    return this.fillTextWithIntegerLength(value);
  }

  private fillTextWithIntegerLength(value: string): string {
    return (Array(this._lengthOfIntegerPart + 1).join(this._emptyValue) + value).slice(-this._lengthOfIntegerPart);
  }

  private toDecimalPart(source: string): string {
    const splitted = source.split(".");
    const value = (1 < splitted.length) ? splitted[1] : "";
    return (value + Array(this._lengthOfDecimalPart + 1).join(this._emptyValue)).substr(0, this._lengthOfDecimalPart);
  }

  private toPair(key: string, source: string, index: number): { key: string, value: string } {
    const value = source.substr(index, 1);
    return {
      key,
      value: (value !== this._emptyValue) ? value : "",
    };
  }

  private readonly _emptyValue = " ";
}
